import { useId, type ComponentPropsWithRef, type ReactNode } from "react";

/** Small accessible primitives shared by every feature. Colours come from CSS variables. */

type Variant = "primary" | "secondary" | "danger" | "ghost";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-accent text-accent-contrast hover:bg-accent-strong",
  secondary: "border border-border bg-surface text-text hover:bg-surface-2",
  danger: "border border-border bg-surface text-danger hover:bg-surface-2",
  ghost: "text-text hover:bg-surface-2",
};

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter((p) => typeof p === "string" && p.length > 0).join(" ");
}

export function Button({
  variant = "secondary",
  className,
  type = "button",
  ...rest
}: ComponentPropsWithRef<"button"> & { variant?: Variant }) {
  return (
    <button
      type={type}
      className={cx(
        "inline-flex min-h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium",
        "transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        className,
      )}
      {...rest}
    />
  );
}

export function TextField({
  label,
  hint,
  className,
  ...rest
}: ComponentPropsWithRef<"input"> & { label: string; hint?: string }) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className={cx("flex flex-col gap-1", className)}>
      <label htmlFor={id} className="text-sm font-medium text-text">
        {label}
      </label>
      <input
        id={id}
        aria-describedby={hint === undefined ? undefined : hintId}
        className="min-h-9 rounded-md border border-border bg-surface px-3 text-sm text-text placeholder:text-muted"
        {...rest}
      />
      {hint === undefined ? null : (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-3 text-sm text-text disabled:opacity-50"
    >
      <span
        aria-hidden="true"
        className={cx(
          "relative inline-block h-5 w-9 rounded-full transition-colors",
          checked ? "bg-accent" : "bg-border",
        )}
      >
        <span
          className={cx(
            "absolute left-0 top-0.5 size-4 rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-4.5" : "translate-x-0.5",
          )}
        />
      </span>
      {label}
    </button>
  );
}

export function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="rounded-lg border border-border bg-surface p-4 sm:p-5">
      <h2 id={id} className="text-base font-semibold text-text">
        {title}
      </h2>
      {description === undefined ? null : <p className="mt-1 text-sm text-muted">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-xl font-semibold text-text">{title}</h1>
      {children}
    </div>
  );
}

/** An inline error, announced to screen readers when it appears. */
export function ErrorText({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-sm text-danger">
      {children}
    </p>
  );
}

/** A polite status line (saved, added...) for screen readers and sighted users alike. */
export function StatusText({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="text-sm text-muted">
      {children}
    </p>
  );
}

export function Splash({ label }: { label: string }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg" role="status" aria-live="polite">
      <p className="text-sm text-muted">{label}</p>
    </div>
  );
}

export function Placeholder({ title, workPackage, children }: { title: string; workPackage: string; children?: ReactNode }) {
  return (
    <div>
      <PageHeader title={title} />
      <div className="rounded-lg border border-dashed border-border p-8 text-center">
        <p className="text-sm text-muted">Coming in {workPackage}.</p>
        {children}
      </div>
    </div>
  );
}
